import * as React from "react"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Semantic badges.
 *
 * Every semantic variant carries an icon slot as well as a colour. Status must
 * survive greyscale, so the colour is a reinforcement and never the only signal.
 * Pass the icon as the first child:
 *
 *   <Badge variant="opportunity"><TrendingUp size={12} /> Low-CTR opportunity</Badge>
 */
const badgeVariants = cva(
  cn(
    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold",
    "transition-colors duration-control ease-editorial",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 focus-visible:ring-offset-offwhite",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground shadow hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow hover:bg-destructive/80",
        outline: "text-foreground",

        /** Emerald means opportunity. It means nothing else. */
        opportunity: "border-emerald-100 bg-emerald-50 text-emerald-700",
        /** Amber means something needs a look. */
        attention: "border-amber-500/25 bg-amber-50 text-amber-700",
        /**
         * Working well. Deliberately blue rather than emerald, so a healthy
         * metric cannot be mistaken for an available opportunity.
         */
        positive: "border-blue-600/20 bg-blue-50 text-blue-600",
        /** Neutral information. */
        info: "border-line bg-offwhite text-slate-600",
        /** Something failed. */
        error: "border-red-600/20 bg-red-50 text-red-600",
        /**
         * New feature or unread marker. emerald-700, not emerald-600: white on
         * 600 measures 3.77:1 and badge text is small.
         */
        new: "border-transparent bg-emerald-700 text-white",
        /** Sample or demo data. Must be visible wherever fictional data appears. */
        demo: "border-line-strong bg-white text-slate-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Badge({
  className,
  variant,
  ...props
}) {
  return (<div className={cn(badgeVariants({ variant }), className)} {...props} />);
}

export { Badge, badgeVariants }
